import { Component, EventEmitter, Output, Input, OnChanges, SimpleChanges, OnDestroy, inject } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { WorkerFileTypeDto } from 'src/app/models/dtos/worker-file-type-dto';
import { LegalizationDocumentFileTypeDto } from '../../../../models/dtos/legalization-document-files-type-dto';

@Component({
  selector: 'app-legalization-file-type-selector',
  templateUrl: './legalization-file-type-selector.component.html',
  standalone: false
})
export class LegalizationFileTypeSelectorComponent implements OnChanges, OnDestroy {
  private dictionaryService = inject(DictionaryService);

  @Output() typeSelected = new EventEmitter<LegalizationDocumentFileTypeDto>();
  @Input() selectedTypeId: number | null = null;
  @Input() set disabled(value: boolean) {
    if (this.legalizationDocumentTypeId) {
      if (value) {
        this.legalizationDocumentTypeId.disable();
      } else {
        this.legalizationDocumentTypeId.enable();
      }
    }
  }

  private readonly unsubscribe$ = new Subject<void>();

  legalizationDocumentTypeId = new UntypedFormControl(null, [Validators.required]);
  filteredDocumentTypes$: Observable<LegalizationDocumentFileTypeDto[]>;
  legalizationDocumentTypes: LegalizationDocumentFileTypeDto[] | null = null;

  private fileTypesSubject = new BehaviorSubject<WorkerFileTypeDto[]>([]);
  legalizationDocumentTypes$: Observable<LegalizationDocumentFileTypeDto[]> = this.fileTypesSubject.pipe(
    switchMap(() => this.dictionaryService.getLegalizationDocumentFileTypes()),
  );


  constructor() {
    this.fileTypesSubject.next([]);
    this.setupTypeFiltering();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedTypeId'] && changes['selectedTypeId'].currentValue !== undefined) {
      this.legalizationDocumentTypeId.setValue(this.selectedTypeId, { emitEvent: false });
    }
  }

  displayFn = (id: number): string => {
    if (!id || !this.legalizationDocumentTypes) {
      return '';
    }
    const docType = this.legalizationDocumentTypes.find(dt => dt.Id === id);
    return docType ? docType.Name : '';
  }

  private setupTypeFiltering() {
    this.filteredDocumentTypes$ = this.legalizationDocumentTypeId.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      startWith(''),
      map(value => typeof value === 'string' ? value : ''),
      switchMap(name => this.legalizationDocumentTypes$.pipe(
        tap(types => this.legalizationDocumentTypes = types),
        map(types => types.filter(type => type.Name.toLowerCase().includes(name.toLowerCase())))
      ))
    );

    this.legalizationDocumentTypeId.valueChanges.pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe(id => {
      const selectedType = this.legalizationDocumentTypes?.find(type => type.Id === id);
      if (selectedType) {
        this.typeSelected.emit(selectedType);
      }
    });

    this.legalizationDocumentTypes$.pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe(types => {
      if (this.selectedTypeId && types.length > 0) {
        const selectedType = types.find(type => type.Id === this.selectedTypeId);
        if (selectedType) {
          this.legalizationDocumentTypeId.setValue(this.selectedTypeId, { emitEvent: true });
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
